import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { PeriodSelector } from './PeriodSelector.tsx';
import axiosInstance from '../axiosInstance.tsx';
import { useIsMobile } from '../App.tsx';

const ChartWrapper = styled.div`
  background-color: var(--primary-color);
  border-radius: 12px;
 
  display: flex;
  flex-direction: column;
  gap: 20px;
    @media (max-width: 768px) {
    gap: 12px; 
    padding: 16px;
  }

`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
       @media (max-width: 768px) {
    gap: 0;
    padding: 0;
    min-height: 40px;
  }
  padding: 24px;
`;

const ChartInfo = styled.div`
  display: flex;
  flex-direction: column;
 gap: 24px;
  border-radius: 16px;
      @media (max-width: 768px) {
    gap: 12px;


  }



`;

const ChartSubInfo = styled.div`
  display: flex;
  flex-direction: column;

 gap: 12px;
 min-height: 68px;

    @media (max-width: 768px) {
    gap: 8px; 

  }

  border-radius: 16px;
`;

const ChartTitle = styled.div`
  font-size: 16px;
      @media (max-width: 768px) {
      
      
    font-size: 12px; 
  }
  font-weight: 500;
  color: var(--text-accent-color);
`;

const ChartValue = styled.div`
  font-size: 22px;
  margin-top: 12px;
  font-weight: 500;
  color: var(--text-color);
  line-height: 30px;
      @media (max-width: 768px) {
          min-width: 20vw;
          margin-top: 0px;
    
    margin-top: auto;
  }
`;

const ChartSubValue = styled.div<{positive: boolean}>`
  font-size: 13px;
    font-weight: 500;
    color: ${({ positive }) => (positive ? "var(--text-active-color)" : "var(--text-error-color)")};
`;



const descriptions = {
  week: "за последнюю неделю",
  month: "за последний месяц",
  day: "за последний день",
  year: "за последний год",
  all: "за все время"
};
function formatLabel(ts: string, period: string) {
  const d = new Date(ts);

  switch (period) {
    case "day":
      // только время
      return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });

    case "week":
      // день.месяц, время
      return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });
    case "month":
      // только день.месяц
      return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });

    case "year":
    case "all":
      // только месяц в сокращённом виде
      return d.toLocaleDateString("ru-RU", { month: "short" });

    default:
      return d.toLocaleString("ru-RU");
  }
}


export default function SourcesChart({ period, setPeriod }) {
  const [chartData, setChartData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalChange, setTotalChange] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`/uploadStats?period=${period}`).then((res) => {
      const stats = res.data.stats;
      const panelNames = Object.keys(stats);

      if (panelNames.length === 0) {
        setChartData([]);
        setTotal(res.data.total);
        setTotalChange(res.data.total_change);
        setLoading(false);
        return;
      }

      const bucketCount = stats[panelNames[0]].length;
      const rows = [];

      for (let i = 0; i < bucketCount; i++) {
        const row: any = {
          label: formatLabel(stats[panelNames[0]][i].timestamp, period),
        };
        for (const panel of panelNames) {
          row[panel] = stats[panel][i].count;
        }
        rows.push(row);
      }

      setChartData(rows);
      setTotal(res.data.total);
      setTotalChange(res.data.total_change);
      setLoading(false);
    });
  }, [period]);
  const isMobile = useIsMobile();



  return (
    <ChartWrapper>
      {isMobile ?       <><ChartHeader>
                <ChartTitle>График заливов по источникам</ChartTitle>
          <PeriodSelector
          period={period}
          onChange={(period) => {
              if(loading) return;
              setPeriod(period);
          }}// блокируем смену периода
        />
    

          </ChartHeader>
        <ChartInfo>
          
          {!loading &&
          <ChartSubInfo>
            <ChartValue>{total.toLocaleString()} успешно</ChartValue>
            <ChartSubValue positive={totalChange>0}>
              {totalChange > 0 ? "+" : ""}
              {totalChange.toLocaleString()} {descriptions[period]}
            </ChartSubValue>
          </ChartSubInfo>
}
        </ChartInfo>

      </> :
      <ChartHeader>
        <ChartInfo>
          <ChartTitle>График заливов по источникам</ChartTitle>
          {!loading &&
          <ChartSubInfo>
            <ChartValue>{total.toLocaleString()} успешно</ChartValue>
            <ChartSubValue positive={totalChange>0}>
              {totalChange > 0 ? "+" : ""}
              {totalChange.toLocaleString()} {descriptions[period]}
            </ChartSubValue>
          </ChartSubInfo>
}
        </ChartInfo>

        <PeriodSelector
          period={period}
          onChange={(period) => {
              if(loading) return;
              setPeriod(period);
          }}// блокируем смену периода
        />
      </ChartHeader>
        
    
    }
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
          <Spinner />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid stroke="#444" strokeDasharray="5 5" />
            <XAxis dataKey="label" stroke="#aaa" />
            <YAxis stroke="#aaa" />
           <Tooltip
  content={({ payload, label, active }) => {
    if (!active || !payload || payload.length === 0) return null;

    // сортировка по значению текущей точки
    const sorted = [...payload].sort((a, b) => b.value - a.value);

    return (
      <div
        style={{
          background: "#0E0E10",
          padding: "8px 12px",
          border: "1px solid #FFFFFF14",
          color: "#fff",
          fontSize: 16,
        }}
      >
        <div style={{ marginBottom: 4, fontWeight: 600 }}>{label}</div>
                  <div
            key="total"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              marginBottom: 2,
            }}
          >
            <span style={{ color: "#fff" }}><b>Всего</b></span>
          <span style={{ color: "#fff", fontWeight: 500 }}>
            {sorted.reduce((acc, { value }) => acc + value, 0)}
          </span>

          </div>
        {sorted.map((p) => (
          <div
            key={p.name}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              marginBottom: 2,
            }}
          >
            <span style={{ color: p.stroke || "#fff" }}>{p.name}</span>
            <span style={{ color: "#fff", fontWeight: 500 }}>
              {p.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }}
/>

            {chartData.length > 0 &&
              Object.keys(chartData[0])
                .filter((k) => k !== "label").sort((a, b) => (chartData[0][b] - chartData[0][a]))
                .map((panel, index) => (
                  <Line
                    key={panel}
                    type="monotone"
                    dataKey={panel}
                    strokeWidth={2}
                    stroke={["#4A90E2", "#FF5733", "#F1C40F", "#8E44AD"][index] || "#fff"}
                  />
                ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartWrapper>
  );
}

// Простой спиннер, можно заменить на твой компонент
const Spinner = () => (
  <div style={{ border: "4px solid #444", borderTop: "4px solid #888", borderRadius: "50%", width: 32, height: 32, animation: "spin 1s linear infinite" }} />
);

// Добавляем CSS-анимацию
const style = document.createElement("style");
style.innerHTML = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
document.head.appendChild(style);